/** 手机布局：弹窗限制动态可视高度并内部滚动，文件行允许换行，避免窄屏被操作区遮住。 */
import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { Megaphone, User, FileText, Loader2, CheckCircle2, CircleDashed, ArrowRight, Download } from "lucide-react";
import { T } from "../../../lib/i18n";
import { fmtT } from "./meta";
import { fmtD } from "../../../lib/usDate";
import { errTextOf } from "../../../lib/detailText";
import FileUploadDate from "./FileUploadDate";

/* 签约前营销方案 · 只读记录（2026-10-20 用户需求）
 *
 * 需求原话：「哪怕已签约了..也要保留是这个人做的签约前的营销方案这个字段或者记录..
 * 也就是说展示还是应该展示出来的..只是在签约前营销方案中增加分类的筛选，展示对应的列表」。
 *
 * 为什么是独立弹窗、而不是打开项目抽屉：
 *   签约后项目的写权限按 `visible_pids` 收口（2026-10-20 已修掉「已完成任务仍赋予可见性」
 *   的泄漏）。如果为了看历史记录就把项目抽屉放进来，抽屉里遍布编辑入口，用户点一下就 403。
 *   所以这里只调 `/projects/{pid}/presign-record`，返回的是纯只读的签约前方案切片：
 *   营销方案设计师、任务完成度、当初提交的方案文件、以及签约转入的原因与操作人。
 *   这样「查得到当初谁做的」和「改不动别人的项目」两件事同时成立。
 */
export const PresignRecordDialog = ({ pid, api, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setD(null); setErr(null);
    api.get(`/design/v3/projects/${pid}/presign-record`)
      .then(({ data }) => { if (live) setD(data); })
      .catch((e) => { if (live) setErr(e?.detailText || e.message); });
    return () => { live = false; };
  }, [pid, api]);
  const p = d?.project || {};
  const info = d?.presignInfo || {};
  /* 文件下载统一走 blob：设计附件在对象存储上带 inline 头，
     直接开新标签会变成空白页（见 2026-10-20 附件打开问题的既有结论），
     这里沿用全局 downloadFile 口径，保证「当初的方案」真的能拿到手。 */
  const download = async (f) => {
    try {
      const { data } = await api.get(`/files/${f.fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = f.name || "方案文件";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const CAT = { open: T("未完成"), done: T("已完成"), signed: T("已签约") };
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="dv3-presign-record">
      <div className="bg-white w-full max-w-xl p-5 space-y-3 rounded-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <Megaphone className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-900">{T("签约前营销方案记录（只读）")}</h3>
            <p className="text-sm text-slate-700 truncate">{p.address || "—"}<span className="text-slate-400"> · {p.projectId || ""}</span></p>
          </div>
          {info.categoryLabel && (
            <span data-testid="dv3-presign-record-cat" data-category={info.category}
              className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${info.category === "open" ? "bg-amber-50 text-amber-700 border-amber-200"
                : info.category === "done" ? "bg-teal-50 text-teal-700 border-teal-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
              {T(info.categoryLabel || CAT[info.category] || "")}
            </span>
          )}
        </div>

        {err ? <p className="text-sm text-red-600 py-6 text-center" data-testid="dv3-presign-record-err">{errTextOf(err)}</p>
          : !d ? <div className="h-40 flex items-center justify-center text-slate-400" data-testid="dv3-presign-record-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>
            : (<>
              {/* 「谁做的」是这次需求的核心：放在最上面一行，单独突出。 */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2" data-testid="dv3-presign-record-designer">
                <p className="text-[10px] text-amber-700 font-semibold">{T("签约前营销方案设计师")}</p>
                <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-amber-600" />{info.designer || p.presignDesigner || T("未记录")}
                </p>
                {!p.presignDesigner && info.designer && (
                  <p className="text-[10px] text-slate-500 mt-0.5">{T("该字段来自签约前阶段 / 任务负责人（历史项目未单独存「营销方案设计师」）")}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5">
                  <p className="text-[10px] text-slate-400">{T("阶段任务完成度")}</p>
                  <p className="font-bold text-slate-800" data-testid="dv3-presign-record-progress">{info.tasksDone || 0}/{info.tasksTotal || 0}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5">
                  <p className="text-[10px] text-slate-400">{T("签约日期")}</p>
                  <p className="font-bold text-slate-800">{p.contractSignDate || "—"}</p>
                </div>
              </div>

              {/* 签约转入轨迹：满足「在系统中留下项目流转的原因和谁操作的」。
                  新数据有 reason / by；历史导入项目没有这条流转记录时明确写「无记录」，
                  不编造原因。 */}
              <div className="rounded-lg border border-slate-200 px-3 py-2" data-testid="dv3-presign-record-conv">
                <p className="text-[10px] font-semibold text-slate-500 flex items-center gap-1"><ArrowRight className="h-3 w-3" />{T("转入签约后")}</p>
                {info.convertedAt ? (
                  <p className="text-[11px] text-slate-700 mt-0.5">
                    {fmtT(info.convertedAt)} · {info.convertedBy || "—"}
                    {info.convertedReason ? <span className="text-slate-500"> · {info.convertedReason}</span> : null}
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-0.5" data-testid="dv3-presign-record-conv-none">{T("无流转记录（历史项目建项即为签约状态）")}</p>
                )}
              </div>

              {(d.tasks || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-slate-600 mb-1">{T("签约前阶段任务")}</p>
                  <div className="space-y-1" data-testid="dv3-presign-record-tasks">
                    {d.tasks.map((t, i) => {
                      const done = ["Completed", "Approved", "Not Applicable"].includes(t.status) || !!t.actualFinish;
                      return (
                        <div key={i} className="flex items-start gap-1.5 text-[11px] py-0.5 border-b border-slate-100 last:border-0">
                          {done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                            : <CircleDashed className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />}
                          <span className="flex-1 min-w-0 text-slate-700">{T(t.title)}</span>
                          <span className="shrink-0 text-slate-500" title={t.autoNote || ""}>{t.primaryOwner || "—"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[11px] font-semibold text-slate-600 mb-1">{T("方案文件（{0}）", [(d.files || []).length])}</p>
                {(d.files || []).length === 0
                  ? <p className="text-[11px] text-slate-400" data-testid="dv3-presign-record-files-none">{T("没有留存方案文件（方案可能只在 CRM 概念方案里提交）")}</p>
                  : <div className="space-y-1" data-testid="dv3-presign-record-files">
                    {d.files.map((f) => (
                      <button key={f.fileId} type="button" onClick={() => download(f)} data-testid={`dv3-presign-record-file-${f.fileId}`}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-[11px] hover:border-violet-300 hover:bg-violet-50/40">
                        <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="flex-1 min-w-0 text-slate-700"><span className="block truncate">{f.name || f.type || "—"}</span><FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="block" /></span>
                        {f.version ? <span className="shrink-0 text-slate-400">v{f.version}</span> : null}
                        {/* 【2026-10-29 时区统一】原为 `String(f.at).slice(0,10)`（UTC 日），改走 fmtD 洛杉矶口径。 */}
                        <span className="shrink-0 text-slate-400">{f.at ? fmtD(f.at) : ""}</span>
                        <Download className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      </button>
                    ))}
                  </div>}
              </div>
            </>)}

        <div className="flex justify-end pt-1">
          <Button size="sm" variant="outline" data-testid="dv3-presign-record-close" onClick={onClose}>{T("关闭")}</Button>
        </div>
      </div>
    </div>
  );
};

export default PresignRecordDialog;
